import type { VarSubject } from "@livcg/schema";

type Ev = { id?: string; type: string; playerId: string; minute: number; second: number };

export type VarCorrection = "rescind" | "downgrade" | "upgrade" | "goal_cancel" | "yellow_cancel";

const GOAL_TYPES = ["goal", "penalty_goal", "own_goal"];
const CARD_TYPES = ["yellow_card", "second_yellow", "red_card"];

/** Which logged events a VAR subject is about. Penalty / ident review a moment
 *  that is not in the log yet — no pick. */
export function varPickTypes(subject: VarSubject): string[] | null {
  if (subject === "goal") return GOAL_TYPES;
  if (subject === "red_card") return CARD_TYPES;
  return null;
}

const at = (e: Ev) => e.minute * 60 + e.second;

/** The events the operator picks from — newest first (the review is usually
 *  about the latest one). */
export function varSubjectEvents<T extends Ev>(subject: VarSubject, events: T[]): T[] {
  const types = varPickTypes(subject);
  if (!types) return [];
  return events.filter((e) => e.id && types.includes(e.type)).sort((a, b) => at(b) - at(a));
}

/** Event types each correction may act on. */
export const CORRECTION_TYPES: Record<VarCorrection, string[]> = {
  goal_cancel: GOAL_TYPES,
  rescind: ["red_card", "second_yellow"],
  // A second yellow cannot be "downgraded" — it already is a yellow.
  downgrade: ["red_card"],
  // A yellow reviewed into a straight red.
  upgrade: ["yellow_card"],
  yellow_cancel: ["yellow_card", "second_yellow"],
};

/** The event a VAR decision corrects: the picked one if it fits the
 *  correction, else (no pick) the player's latest fitting event. */
export function correctionTarget<T extends Ev>(
  kind: VarCorrection,
  events: T[],
  eventId: string | null,
  playerId: string | null,
): T | undefined {
  const types = CORRECTION_TYPES[kind];
  if (eventId) {
    const picked = events.find((e) => e.id === eventId);
    return picked && types.includes(picked.type) ? picked : undefined;
  }
  if (!playerId) return undefined;
  return events
    .filter((e) => e.playerId === playerId && types.includes(e.type))
    .sort((a, b) => at(a) - at(b))
    .at(-1);
}
